import React from 'react';
import PropTypes from 'prop-types';

/**
 * StatBar - Kompakte Statistics-Anzeige in einer Leiste
 * 
 * Ersetzt die StatCardGrid für eine kompaktere Darstellung.
 * 
 * Usage:
 * <StatBar
 *   stats={[
 *     { value: 5, label: "Kurse", icon: "📚", color: "primary" },
 *     { value: 12, label: "Vorlagen", icon: "📝", color: "secondary" },
 *     { value: 3, label: "Aktiv", icon: "▶️", color: "success" }
 *   ]}
 * />
 * 
 * Rendering:
 * ┌──────────────────────────────────────────────────────────────┐
 * │  📚 5 Kurse    │    📝 12 Vorlagen    │    ▶️ 3 Aktiv        │
 * └──────────────────────────────────────────────────────────────┘
 */

export default function StatBar({
  stats,
  variant = 'default',
  size = 'md',
  className = ''
}) {
  // Color classes for value
  const colorClasses = {
    primary: 'text-primary-600 dark:text-primary-400',
    secondary: 'text-secondary-600 dark:text-secondary-400',
    success: 'text-success-600 dark:text-success-400',
    warning: 'text-warning-600 dark:text-warning-400',
    danger: 'text-danger-600 dark:text-danger-400',
    default: 'text-gray-900 dark:text-white'
  };

  // Size classes
  const sizeClasses = {
    sm: {
      container: 'py-2 px-3',
      icon: 'text-lg',
      value: 'text-lg font-bold',
      label: 'text-xs'
    },
    md: {
      container: 'py-3 px-4',
      icon: 'text-xl',
      value: 'text-xl font-bold',
      label: 'text-sm'
    },
    lg: {
      container: 'py-4 px-6',
      icon: 'text-2xl',
      value: 'text-2xl font-bold',
      label: 'text-base'
    }
  };

  // Variant classes
  const variantClasses = {
    default: 'bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm',
    compact: 'bg-gray-50 dark:bg-gray-800/30 rounded-lg',
    transparent: ''
  };

  const sizes = sizeClasses[size];

  return (
    <div className={`${variantClasses[variant]} ${className}`}>
      <div className={`flex items-center justify-between divide-x divide-gray-200 dark:divide-gray-700 ${sizes.container}`}>
        {stats.map((stat, index) => (
          <div 
            key={index}
            className={`flex items-center gap-2 flex-1 justify-center ${index > 0 ? 'pl-4' : ''}`}
          >
            {/* Icon */}
            {stat.icon && (
              <span className={sizes.icon}>{stat.icon}</span>
            )}
            
            {/* Value + Label */}
            <div className="flex items-baseline gap-1.5">
              <span className={`${sizes.value} ${colorClasses[stat.color] || colorClasses.default}`}>
                {typeof stat.value === 'number' ? stat.value.toLocaleString('de-DE') : stat.value}
              </span>
              <span className={`${sizes.label} text-gray-500 dark:text-gray-400`}>
                {stat.label}
              </span>
            </div>
            
            {/* Trend (optional) */}
            {stat.trend && (
              <span className={`text-xs font-medium ${stat.trendUp ? 'text-success-600' : 'text-danger-600'}`}>
                {stat.trendUp ? '↑' : '↓'} {stat.trend}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

StatBar.propTypes = {
  stats: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
    color: PropTypes.oneOf(['primary', 'secondary', 'success', 'warning', 'danger', 'default']),
    trend: PropTypes.string,
    trendUp: PropTypes.bool
  })).isRequired,
  variant: PropTypes.oneOf(['default', 'compact', 'transparent']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string
};

/**
 * StatBarVertical - Vertikale Variante für Sidebars
 */
export function StatBarVertical({
  stats,
  className = ''
}) {
  return (
    <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm divide-y divide-gray-200 dark:divide-gray-700 ${className}`}>
      {stats.map((stat, index) => (
        <div key={index} className="flex items-center gap-3 p-4">
          {stat.icon && (
            <span className="text-2xl">{stat.icon}</span>
          )}
          <div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {typeof stat.value === 'number' ? stat.value.toLocaleString('de-DE') : stat.value}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {stat.label}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

StatBarVertical.propTypes = {
  stats: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.string
  })).isRequired,
  className: PropTypes.string
};
